import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useSearchParams } from 'react-router-dom';
import { NetworkStatus, useQuery } from '@apollo/client';
import Avatar from '../components/Avatar';
import Banner from '../components/Banner';
import { Section, SectionItem, SectionName } from '../components/Chrome';
import {
  Drawer,
  DrawerLink,
  DrawerList,
  DrawerTrigger,
} from '../components/Drawer';
import Inline from '../components/Inline';
import { List, ListCell, ListItem } from '../components/List';
import Loader from '../components/Loader';
import { Tags, TagsItem } from '../components/Tags';
import Tooltip from '../components/Tooltip';
import Icon from '../components/Icon';
import { SPACE, SPACE_TAGS, SPACE_MEMBERS } from '../utilities/queries';
import { getDate } from '../utilities/getDate';
import { getRelativeTime } from '../utilities/getRelativeTime';
import { useSpaceContext } from '../contexts/space';

const SpacePage = () => {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [fetchMoreEnabled, setFetchMoreEnabled] = useState(true);

  const { spaceVars } = useSpaceContext();

  const {
    data: spaceData,
    refetch,
    networkStatus,
    fetchMore,
  } = useQuery(SPACE, {
    variables: {
      slug: params.s,
      ...spaceVars,
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (
        data.space.topics &&
        (data.space.topics.length === 0 ||
          data.space.topics.length !== spaceVars.limit)
      ) {
        setFetchMoreEnabled(false);
      } else {
        setFetchMoreEnabled(true);
      }
    },
  });
  const { data: spaceTagsData } = useQuery(SPACE_TAGS, {
    variables: { slug: params.s },
  });
  const { data: spaceMembersData } = useQuery(SPACE_MEMBERS, {
    variables: { slug: params.s },
  });

  const unfoundFilters = () => {
    const all = [
      searchParams.get('author') && 'Author',
      searchParams.get('state') && 'State',
      searchParams.get('assignee') && 'Assignee',
      searchParams.get('tag') && 'Tag',
    ].filter(Boolean);

    return `${all.join(', ')} ${all.length === 1 ? 'filter' : 'filters'}`;
  };

  useEffect(() => {
    const handleScroll = () => {
      const doc = document.documentElement;

      if (
        fetchMoreEnabled &&
        networkStatus !== 3 &&
        doc.scrollTop + doc.clientHeight >= doc.scrollHeight
      ) {
        fetchMore({
          variables: {
            after:
              spaceData &&
              spaceData.space.topics[spaceData.space.topics.length - 1].id,
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            return {
              space: {
                ...previousResult.space,
                topics: [
                  ...previousResult.space.topics,
                  ...fetchMoreResult.space.topics,
                ],
              },
            };
          },
        });
      }
    };

    addEventListener('scroll', handleScroll);

    return () => {
      removeEventListener('scroll', handleScroll);
    };
  });

  return (
    <Section>
      {!spaceData ||
      !spaceTagsData ||
      !spaceMembersData ||
      networkStatus === NetworkStatus.refetch ? (
        <>
          <Loader />
        </>
      ) : (
        <>
          <Helmet>
            <title>{spaceData?.space?.name}</title>
          </Helmet>
          {spaceData.space?.id ? (
            <>
              <SectionName>{spaceData.space.name}</SectionName>
              {spaceData.space.planLevel >= 1 && spaceData.space?.bannerUrl && (
                <Banner
                  src={spaceData.space.bannerUrl}
                  alt={`${spaceData.space.name} banner image`}
                />
              )}
              <div
                style={{
                  overflow: 'auto hidden',
                  paddingBottom: '1px',
                }}
              >
                <List>
                  <ListItem isHead>
                    <ListCell style={{ width: '48px' }} />
                    <ListCell>ID</ListCell>
                    <ListCell isExtended>Name</ListCell>
                    <ListCell>Created</ListCell>
                    <Drawer>
                      <DrawerTrigger>
                        <ListCell
                          isActive={searchParams.get('author')}
                          isButton
                        >
                          <Inline>Author</Inline>
                          <Icon name="arrow-down" />
                        </ListCell>
                      </DrawerTrigger>
                      <DrawerList>
                        <DrawerLink
                          as="button"
                          isActive={searchParams.get('author') == null}
                          onClick={() => {
                            refetch({ authorId: null });
                            searchParams.delete('author');
                            setSearchParams(searchParams);
                          }}
                        >
                          All
                        </DrawerLink>
                        {spaceMembersData?.space?.members.map((user) => (
                          <DrawerLink
                            as="button"
                            key={user.user.id}
                            isActive={
                              searchParams.get('author') === user.user.id
                            }
                            onClick={() => {
                              refetch({ authorId: user.user.id });
                              searchParams.set('author', user.user.id);
                              setSearchParams(searchParams);
                            }}
                          >
                            {user.user.name}
                          </DrawerLink>
                        ))}
                      </DrawerList>
                    </Drawer>
                    <Drawer>
                      <DrawerTrigger>
                        <ListCell isActive={searchParams.get('state')} isButton>
                          <Inline>State</Inline>
                          <Icon name="arrow-down" />
                        </ListCell>
                      </DrawerTrigger>
                      <DrawerList>
                        <DrawerLink
                          as="button"
                          isActive={searchParams.get('state') === null}
                          onClick={() => {
                            refetch({ state: null });
                            searchParams.delete('state');
                            setSearchParams(searchParams);
                          }}
                        >
                          All
                        </DrawerLink>
                        <DrawerLink
                          as="button"
                          isActive={searchParams.get('state') === 'OPEN'}
                          onClick={() => {
                            refetch({ state: 'OPEN' });
                            searchParams.set('state', 'OPEN');
                            setSearchParams(searchParams);
                          }}
                        >
                          Open
                        </DrawerLink>
                        <DrawerLink
                          as="button"
                          isActive={searchParams.get('state') === 'CLOSED'}
                          onClick={() => {
                            refetch({ state: 'CLOSED' });
                            searchParams.set('state', 'CLOSED');
                            setSearchParams(searchParams);
                          }}
                        >
                          Closed
                        </DrawerLink>
                      </DrawerList>
                    </Drawer>
                    <Drawer>
                      <DrawerTrigger>
                        <ListCell
                          isActive={searchParams.get('assignee')}
                          isButton
                        >
                          <Inline>Assignee</Inline>
                          <Icon name="arrow-down" />
                        </ListCell>
                      </DrawerTrigger>
                      <DrawerList>
                        <DrawerLink
                          as="button"
                          isActive={searchParams.get('assignee') === null}
                          onClick={() => {
                            refetch({ assigneeId: null });
                            searchParams.delete('assignee');
                            setSearchParams(searchParams);
                          }}
                        >
                          All
                        </DrawerLink>
                        {spaceMembersData?.space?.members.map((user) => (
                          <DrawerLink
                            as="button"
                            key={user.user.id}
                            isActive={
                              searchParams.get('assignee') === user.user.id
                            }
                            onClick={() => {
                              refetch({ assigneeId: user.user.id });
                              searchParams.set('assignee', user.user.id);
                              setSearchParams(searchParams);
                            }}
                          >
                            {user.user.name}
                          </DrawerLink>
                        ))}
                      </DrawerList>
                    </Drawer>
                    <Drawer>
                      <DrawerTrigger>
                        <ListCell isActive={searchParams.get('tag')} isButton>
                          <Inline>Tags</Inline>
                          <Icon name="arrow-down" />
                        </ListCell>
                      </DrawerTrigger>
                      <DrawerList>
                        <DrawerLink
                          as="button"
                          isActive={searchParams.get('tag') === null}
                          onClick={() => {
                            refetch({ tagId: null });
                            searchParams.delete('tag');
                            setSearchParams(searchParams);
                          }}
                        >
                          All
                        </DrawerLink>
                        {spaceTagsData?.space?.tags.map((localTag) => (
                          <DrawerLink
                            as="button"
                            onClick={() => {
                              refetch({ tagId: localTag.id });
                              searchParams.set('tag', localTag.id);
                              setSearchParams(searchParams);
                            }}
                            isActive={searchParams.get('tag') === localTag.id}
                            key={localTag.id}
                          >
                            {localTag.name}
                          </DrawerLink>
                        ))}
                      </DrawerList>
                    </Drawer>
                  </ListItem>
                  {networkStatus === 4 ? (
                    <SectionItem>
                      <Loader />
                    </SectionItem>
                  ) : (
                    spaceData.space.topics.map((topic) => (
                      <ListItem
                        key={topic.id}
                        to={`/s/${spaceData.space.slug}/t/${topic.publicId}`}
                        state={{
                          filters: {
                            author: searchParams.get('author'),
                            state: searchParams.get('state'),
                            assignee: searchParams.get('assignee'),
                            tag: searchParams.get('tag'),
                          },
                        }}
                        $hasTag={topic.tags.length !== 0}
                      >
                        <ListCell>
                          {topic.isPinned ? (
                            <Icon name="pin" title="Pinned topic" />
                          ) : (
                            <Icon name="topic" title="Basic topic" />
                          )}
                        </ListCell>
                        <ListCell>{topic.publicId}</ListCell>
                        <ListCell isExtended>{topic.name}</ListCell>
                        <ListCell>
                          <Tooltip
                            text={getDate(topic.createdAt)}
                            label={`Created: ${getDate(topic.createdAt)}`}
                          >
                            <Inline>{getRelativeTime(topic.createdAt)}</Inline>
                          </Tooltip>
                        </ListCell>
                        <ListCell>
                          <Tooltip
                            text={topic.author.name}
                            label={`Author: ${topic.author.name}`}
                          >
                            <Inline>
                              <Avatar src={topic.author.avatarUrl} isSmall />
                            </Inline>
                          </Tooltip>
                        </ListCell>
                        <ListCell>
                          <Inline
                            aria-label={`State: ${topic.state}`}
                            style={{ display: 'flex' }}
                          >
                            {topic.state === 'Open' && <Icon name="state" />}
                            {topic.state === 'Closed' && (
                              <Icon name="state-closed" />
                            )}
                          </Inline>
                        </ListCell>
                        <ListCell>
                          <Tooltip
                            text={topic.assignee && topic.assignee.name}
                            label={
                              topic.assignee &&
                              `Assignee: ${topic.assignee.name}`
                            }
                          >
                            <Inline>
                              {topic.assignee ? (
                                <Avatar
                                  src={topic.assignee.avatarUrl}
                                  isSmall
                                />
                              ) : (
                                '-'
                              )}
                            </Inline>
                          </Tooltip>
                        </ListCell>
                        <ListCell>
                          <Tags
                            inList
                            aria-label={
                              topic.tags.length !== 0
                                ? `Tags: ${topic.tags
                                    .map((tag) => tag.name)
                                    .join(', ')}`
                                : null
                            }
                          >
                            {topic.tags.map((tag) => (
                              <TagsItem key={tag.id}>{tag.name}</TagsItem>
                            ))}
                            {topic.tags.length === 0 && '-'}
                          </Tags>
                        </ListCell>
                      </ListItem>
                    ))
                  )}
                </List>
                {networkStatus === 3 && (
                  <SectionItem>
                    <Loader />
                  </SectionItem>
                )}
                {spaceData.space.topics.length === 0 && (
                  <>
                    {searchParams.get('author') ||
                    searchParams.get('state') ||
                    searchParams.get('assignee') ||
                    searchParams.get('tag') ? (
                      <SectionItem>
                        No topics were found using the {unfoundFilters()}.
                      </SectionItem>
                    ) : (
                      <SectionItem>
                        There are no topics in this space — yet.
                      </SectionItem>
                    )}
                  </>
                )}
              </div>
            </>
          ) : (
            <SectionItem>There is no space with that url.</SectionItem>
          )}
        </>
      )}
    </Section>
  );
};

export default SpacePage;
